import { createDocumentResource, createUnavailableDocumentResource, type DocumentResource } from "@/lib/document-resource";
import { base64ToBlob } from "@/lib/file-bytes";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { type ApolloCache } from "@apollo/client/cache";
import { useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { BackButton } from "@/components/common/BackButton";
import { MarkdownText } from "@/components/common/MarkdownText";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { DocumentView } from "@/components/documents/DocumentView";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  DocumentPickerDialog,
  type PickedDocument,
} from "@/components/share/DocumentPickerDialog";
import { ShareCreateEntryChoice } from "@/components/share/ShareCreateEntryChoice";
import { ShareIcon } from "@/components/share/ShareItemCard";
import { recipientIds, recipientRelations } from "@/lib/content-sharing";
import { ContentSharingDocument } from "@/graphql/generated/operations";
import { SharedItemSharingFields } from "./SharedItemSharingFields";
import { itemTypeLabel } from "@/lib/share-item";
import {
  actingAsCompanyOnOwnContentMessage,
  actingAsDeclaration,
  companyFrozenMessage,
  notActingAsCompanyMessage,
} from "@/lib/share-item";
import { useCompanyContext } from "@/pages/company/use-company-context";
import { formatDateTime } from "@/lib/format-date";
import { ingestFiles, type IngestedFile } from "@/lib/file-ingest";
import { PROJECT_DOCUMENT_ACCEPT, MAX_SHARE_ATTACHMENT_BYTES, MAX_SHARE_IMAGE_BYTES } from "@/lib/upload-limits";
import { logApplicationFailure } from "@/lib/user-error";
import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { usePendingEdits, usePendingScope } from "@/lib/pending-edits";
import { userErrorMessage } from "@/lib/user-error";
import { ProjectMockPage } from "@/pages/project/ProjectMockPage";
import {
  CreateSharedItemDocument,
  ContentSharingPolicyDocument,
  MyselfDocument,
  SharedItemEditDataDocument,
  SharedItemFileContentsDocument,
  type SharedItemEditDataQuery,
  UpdateSharedItemDocument,
} from "@/graphql/generated/operations";

// SHARE · Create - own page for creating a SharedItem. Fields map to the
// existing SharedItem fields (Titel→name, Kurzbeschreibung→shortDescription,
// Beschreibung→description, Bilder→images, Anhänge→files). 'Vorschau'
// shows the item in the Discover detail layout before saving.
//
// Doubles as the editor (route /share/edit/:itemId): prefilled from
// SharedItemEditData, everything editable except the item type. Existing
// images/files carry their file id; removing one queues it for
// imagesToDelete/filesToDelete on update.
//
// On creation, "Project" opens the persisted Project prototype; "Post" uses
// the existing SharedItem form. Editing a SharedItem keeps the existing form.
// Project is a separate prototype entity, not SharedItemType.PROJECT. Its
// company ownership and relationship to SharedItem remain open; see
// docs/project-prototype.md.

// Exported so the Project · Open mask (pages/project) renders literally the
// same field look as this form instead of a copied class string that drifts.
// There is no shared input primitive in components/design yet; when one lands,
// both call sites should move onto it together.
export const SHARE_FORM_INPUT_CLASS =
  "w-full rounded-md bg-page px-3 py-2 text-smycles-body outline-none ring-1 ring-ink/10 transition focus:ring-ink-accent-ui";

const inputCls = SHARE_FORM_INPUT_CLASS;

const SHARE_SCOPE_FORM = "share-form";

export interface UploadedFile {
  document?: DocumentResource;
  name: string;
  mimeType: string;
  content: string;
  preview: string;
  existingId?: string;
  /** Where an existing file lives on the item today (mainImage vs. gallery). */
  existingRole?: "main" | "gallery";
  /** Selected as the item's mainImage (Vorschaubild). Exactly one per image list. */
  isMain?: boolean;
}

/** Exactly one image carries isMain; defaults to the first one. */
function normalizeMain(list: UploadedFile[]): UploadedFile[] {
  if (list.length === 0) return list;
  const mains = list.filter((f) => f.isMain).length;
  if (mains === 1) return list;
  if (mains === 0) return list.map((f, i) => (i === 0 ? { ...f, isMain: true } : f));
  let seen = false;
  return list.map((f) => {
    if (!f.isMain) return f;
    if (seen) return { ...f, isMain: false };
    seen = true;
    return f;
  });
}

function toUploadedFile(f: IngestedFile): UploadedFile {
  return {
    name: f.name,
    mimeType: f.mimeType,
    content: f.content,
    preview: `data:${f.mimeType};base64,${f.content}`,
  };
}

type ItemType = "PROJECT" | "POST";

type EditItem = NonNullable<SharedItemEditDataQuery["sharedItem"]>;

// Initial image/file lists derived from a loaded SharedItem (edit mode). Pure,
// so the form can seed useState from them instead of a prefill setState effect
// (#78). Returns empty lists in create mode (editItem null).
function initialImages(editItem: EditItem | null, intl: IntlShape): UploadedFile[] {
  if (!editItem) return [];
  const galleryImages: UploadedFile[] = (editItem.images ?? []).map((f, i) => ({
    name: intl.formatMessage(
      { id: "share.create.existingImage", defaultMessage: "Image {n}" },
      { n: i + 1 },
    ),
    mimeType: f.mimeType ?? "application/octet-stream",
    content: f.content ?? "",
    preview: f.content ? `data:${f.mimeType};base64,${f.content}` : "",
    existingId: f.id,
    existingRole: "gallery" as const,
  }));
  const mainImage: UploadedFile[] = editItem.mainImage?.content
    ? [
        {
          name: intl.formatMessage({
            id: "share.create.mainImageBadge",
            defaultMessage: "Preview image",
          }),
          mimeType: editItem.mainImage.mimeType ?? "application/octet-stream",
          content: editItem.mainImage.content,
          preview: `data:${editItem.mainImage.mimeType};base64,${editItem.mainImage.content}`,
          existingId: editItem.mainImage.id,
          existingRole: "main" as const,
          isMain: true,
        },
      ]
    : [];
  return normalizeMain([...mainImage, ...galleryImages]);
}

function initialFiles(editItem: EditItem | null, intl: IntlShape, client: ReturnType<typeof useApolloClient>): UploadedFile[] {
  if (!editItem) return [];
  return (editItem.files ?? []).map((f, i) => ({
    document: createDocumentResource({ name: f.fileName?.trim() || `document.${f.fileExtension?.replace(/^\./, "") ?? "bin"}`, mimeType: f.mimeType, load: async () => {
      const { data } = await client.query({ query: SharedItemFileContentsDocument, variables: { id: editItem.id }, fetchPolicy: "no-cache" });
      const original = data?.sharedItem?.files?.find(file => file?.id === f.id);
      if (!original?.content) throw new Error("Missing attachment content");
      return base64ToBlob(original.content, original.mimeType ?? "application/octet-stream");
    } }),
    name:
      f.fileName?.trim() ||
      (intl.formatMessage(
        { id: "share.create.existingAttachment", defaultMessage: "Attachment {n}" },
        { n: i + 1 },
      ) + (f.fileExtension ? ` (${f.fileExtension})` : "")),
    mimeType: f.mimeType ?? "application/octet-stream",
    content: "",
    preview: "",
    existingId: f.id,
  }));
}

export function ShareCreatePage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, loading } = useQuery(SharedItemEditDataDocument, {
    variables: { id: itemId ?? "" },
    skip: !itemId,
    fetchPolicy: "network-only",
  });
  // A REFETCH MUST NOT UNMOUNT THE FORM. Apollo 4 reports a refetch as `loading` while the answer
  // is still there, and gating on `loading` alone swapped the form for the loading line and took
  // every unsaved edit with it. So only a missing answer shows the loading line.
  //
  // A CACHE RESET DOES, deliberately. Changing the acting company empties the cache
  // (@adr SPARK-0034-acting-company-context-resets-the-apollo-cache@3) and the answer is gone with
  // it; the form then remounts from the new context's answer. Carrying the old one through (`previousData`)
  // would seed the form from what was read under the previous context — and the unsaved edits are
  // not lost by this: the acting-company gate has asked about them before the switch.
  const editItem = data?.sharedItem ?? null;
  // Bumped to remount the form from its seed (see ShareCreateForm's onReset).
  const [generation, setGeneration] = useState(0);
  // Entry-choice gate: only asked on real creation (no itemId), never when
  // editing an existing item. The SHARE landing already asks this before
  // ever navigating here (?type= on the URL) - that case skips the gate.
  // null = not decided yet (arriving here directly, e.g. a bookmark), show
  // the modal as a fallback.
  const typeParam = searchParams.get("type");
  const [entryChoice, setEntryChoice] = useState<"post" | "project" | null>(
    typeParam === "project" ? "project" : typeParam === "post" ? "post" : null,
  );
  // Without share.projects a project choice counts as undecided, so ?type=project asks again instead of
  // opening a mask whose save genesis rejects (genesis#402). The share route's RequireFeature holds this
  // page until the whitelist's first answer, so this false is never the loading window.
  const canCreateProject = useHasFeature(FEATURE_CATALOG.shareProjects.key);
  const choice = entryChoice === "project" && !canCreateProject ? null : entryChoice;

  // Gate render until edit data is in, so the form seeds its state from the
  // loaded item via useState initializers rather than a setState-in-effect
  // (#78). The key remounts the form (fresh state) when switching entity.
  if (itemId && loading && !editItem) {
    return (
      <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
        <div className="mb-5">
          <BackButton />
        </div>
        <div className="text-smycles-body text-ink-muted">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </div>
      </div>
    );
  }

  // The gated form always mounts underneath - Modal's blur backdrop is meant
  // to show the platform through it (see Modal.tsx's own comment), which only
  // works if there's a real page behind it, not a blank one. "project" swaps
  // the base content for ProjectMockPage; the choice modal, while open, sits
  // on top of whichever base is showing.
  const showGate = !itemId && choice === null;
  const base =
    !itemId && choice === "project" ? (
      <ProjectMockPage />
    ) : (
      <ShareCreateForm
        key={`${itemId ?? "new"}:${generation}`}
        itemId={itemId}
        editItem={editItem}
        fixedItemType={!itemId ? "POST" : undefined}
        onReset={() => setGeneration((g) => g + 1)}
      />
    );

  return (
    <>
      {base}
      {showGate ? (
        <ShareCreateEntryChoice onChoose={setEntryChoice} onCancel={() => navigate("/share")} />
      ) : null}
    </>
  );
}

function ShareCreateForm({
  itemId,
  editItem,
  fixedItemType,
  onReset,
}: {
  itemId?: string;
  editItem: EditItem | null;
  // Set when the entry-choice gate already decided (grill-me session): the
  // type picker below then shows the same static badge as the edit path
  // instead of a toggle, so the earlier choice can't be silently overridden.
  fixedItemType?: ItemType;
  /**
   * Remount the form from its seed: an empty form on create, the loaded item on edit. Called after
   * every successful save — Publish and the dialog's Save both leave the form right after — and on
   * Discard, which must actually drop the edits: a refetch no longer unmounts the form, so nothing
   * else would.
   */
  onReset: () => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const editing = Boolean(itemId);
  const policy = useQuery(ContentSharingPolicyDocument, { fetchPolicy: "network-only" });
  const sharing = useQuery(ContentSharingDocument, { fetchPolicy: "network-only" });
  const [sharingEdits, setSharingEdits] = useState<string[] | null>(null);
  const [publiclyReadable, setPubliclyReadable] = useState(editing ? editItem?.publiclyReadable === true : true);
  const requiredDefaults = policy.data?.systemSettings?.sharedItemsRequireDefaultRecipients !== false;
  const defaultGroups = sharing.data?.defaultContentSharingRecipients ?? [];
  const recipients = new Map<string, { id: string; name: string; eligible: boolean }>();
  for (const group of [...(editItem?.sharedWith ?? []), ...defaultGroups, ...(sharing.data?.contentSharingRecipients ?? [])]) {
    recipients.set(group.id, group);
  }
  const selectedGroups = sharingEdits ?? recipientIds(editing ? editItem?.sharedWith : defaultGroups);
  const onlyOwner = selectedGroups.length === 0 && !publiclyReadable;
  const sharingError = sharing.error ?? policy.error;
  const sharingUnavailable = sharing.loading || policy.loading || !!sharingError;
  const { data: myselfData } = useQuery(MyselfDocument);
  const me = myselfData?.myself ?? null;
  const myFullName = me?.name
    ? [me.name.prefix, (me.name.given ?? []).filter(Boolean).join(" "), me.name.family, me.name.suffix]
        .filter(Boolean)
        .join(" ")
        .trim()
    : "";

  // Whose voice this post is written in. Null means the person's own — see the create branch.
  const { active: activeCompany } = useCompanyContext();

  // Carry Genesis's Company eligibility into the write context; platform Roles grant no bypass.
  const actingAsCompanyId = activeCompany?.canAdminister ? activeCompany.id : null;
  const [createItem, { loading: creating }] = useMutation(CreateSharedItemDocument);
  const [updateItem, { loading: updating }] = useMutation(UpdateSharedItemDocument);
  const loading = creating || updating;

  const [name, setName] = useState(() => editItem?.name ?? "");
  const [shortDescription, setShortDescription] = useState(() => editItem?.shortDescription ?? "");
  const [description, setDescription] = useState(() => editItem?.description ?? "");
  const [images, setImages] = useState<UploadedFile[]>(() => initialImages(editItem, intl));
  const client = useApolloClient();
  const [files, setFiles] = useState<UploadedFile[]>(() => initialFiles(editItem, intl, client));
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [itemType, setItemType] = useState<ItemType>(
    () => fixedItemType ?? (editItem?.itemType as ItemType) ?? "PROJECT",
  );
  // PREVIEW IS A HISTORY ENTRY (?preview), so ← and the browser's back return to the form instead
  // of leaving it. As component state it had no entry of its own, and "back" from the preview left
  // the page — with every edit in it. UnsavedGuard only holds a change of pathname, so stepping
  // between form and preview never asks.
  const preview = searchParams.has("preview");
  function togglePreview() {
    if (!preview) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("preview", "1");
          return next;
        },
        { state: { previewFromForm: true } },
      );
    } else if ((location.state as { previewFromForm?: boolean } | null)?.previewFromForm) {
      // Pop the entry the form pushed, so the history does not grow a form entry per round trip.
      navigate(-1);
    } else {
      // Arrived on ?preview from outside (a pasted or bookmarked link): there is no form entry
      // behind it to go back to. A reload keeps the history state, so it takes the branch above.
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("preview");
          return next;
        },
        { replace: true },
      );
    }
  }
  const [error, setError] = useState<string | null>(null);
  const [docPicker, setDocPicker] = useState<"image" | "file" | null>(null);

  // Unsaved = anything differs from what the form was seeded with. Read on the first render, so the
  // baseline is exactly the seeded state; a save or discard remounts the form rather than moving it.
  const snapshot = formSnapshot({ name, shortDescription, description, images, files, publiclyReadable });
  const [baseline] = useState(snapshot);
  const sharingDirty =
    !actingAsCompanyId &&
    sharingEdits !== null &&
    !sameIds(sharingEdits, recipientIds(editing ? editItem?.sharedWith : defaultGroups));
  const dirty = snapshot !== baseline || sharingDirty;

  // Registered with the PendingEdits store, so UnsavedGuard asks before ←, a link, Cancel or a
  // reload drops the form, and the acting-company gate asks before a switch does. `ownSaveAction`
  // keeps the SaveBar away: Publish is this page's save.
  const settleScope = usePendingScope(SHARE_SCOPE_FORM, {
    dirty,
    ownSaveAction: true,
    save: async () => {
      await persist();
      onReset();
    },
    discard: onReset,
  });

  function changeImages(next: UploadedFile[]) {
    const kept = new Set(next.map((f) => f.existingId).filter(Boolean));
    // Only gallery files ride imagesToDelete; the mainImage slot has no
    // delete input and is replaced by sending a new mainImage instead.
    setRemovedImageIds((cur) => [
      ...cur,
      ...images
        .filter((f) => f.existingId && f.existingRole === "gallery" && !kept.has(f.existingId))
        .map((f) => f.existingId as string),
    ]);
    setImages(normalizeMain(next));
  }

  function appendImages(next: UploadedFile[]) {
    setImages((cur) => normalizeMain([...cur, ...next]));
  }

  function selectMainImage(index: number) {
    setImages((cur) => cur.map((f, i) => ({ ...f, isMain: i === index })));
  }

  function changeFiles(next: UploadedFile[]) {
    const kept = new Set(next.map((f) => f.existingId).filter(Boolean));
    setRemovedFileIds((cur) => [
      ...cur,
      ...files
        .filter((f) => f.existingId && !kept.has(f.existingId))
        .map((f) => f.existingId as string),
    ]);
    setFiles(next);
  }

  function appendFiles(next: UploadedFile[]) {
    setFiles((cur) => [...cur, ...next]);
  }

  function addPickedDocuments(picked: PickedDocument[]) {
    // base64 length * 3/4 ≈ raw byte size; same caps as the direct upload path.
    const rawBytes = (f: PickedDocument) => Math.floor(f.content.length * 0.75);
    const isImage = (f: PickedDocument) => f.mimeType.startsWith("image/");
    const limitFor = (f: PickedDocument) =>
      isImage(f) ? MAX_SHARE_IMAGE_BYTES : MAX_SHARE_ATTACHMENT_BYTES;
    const tooBig = picked.filter((f) => rawBytes(f) > limitFor(f));
    const firstTooBig = tooBig[0];
    setError(
      firstTooBig
        ? intl.formatMessage(
            {
              id: "share.create.tooLarge",
              defaultMessage: "Too large (max. {max} MB per file): {names}",
            },
            {
              max: Math.floor(limitFor(firstTooBig) / (1024 * 1024)),
              names: tooBig.map((f) => f.name).join(", "),
            },
          )
        : null,
    );
    const ok = picked.filter((f) => rawBytes(f) <= limitFor(f));
    const imgs = ok.filter(isImage);
    const rest = ok.filter((f) => !isImage(f));
    if (imgs.length) setImages((cur) => normalizeMain([...cur, ...imgs]));
    if (rest.length) setFiles((cur) => [...cur, ...rest]);
  }

  /**
   * Write the form to genesis and resolve with the item's id, or reject with a LocalizedError saying
   * why not. Shared by Publish and by the unsaved-changes dialog, which shows the rejection.
   *
   * ONE WRITE AT A TIME. A second write while one runs would create the post twice — or, on edit,
   * upload the new files twice. Publish is counted as a running save (trackSave), which keeps the
   * leave dialog's Save closed meanwhile; this join is the floor under that, for any caller that
   * gets through anyway.
   */
  const inFlight = useRef<Promise<string | null> | null>(null);
  function persist(): Promise<string | null> {
    inFlight.current ??= write().finally(() => {
      inFlight.current = null;
    });
    return inFlight.current;
  }

  async function write(): Promise<string | null> {
    if (!name.trim()) {
      throw new LocalizedError(
        intl.formatMessage({ id: "share.create.titleRequired", defaultMessage: "Add a title before saving." }),
      );
    }
    if (!actingAsCompanyId && sharingUnavailable) {
      throw new LocalizedError(
        intl.formatMessage({
          id: "share.create.recipientsUnavailable",
          defaultMessage: "The recipients are not available yet, so this cannot be saved.",
        }),
      );
    }
    const main = images.find((f) => f.isMain) ?? images[0] ?? null;
    const asInput = (f: UploadedFile) => ({ fileName: f.name, mimeType: f.mimeType, content: f.content });
    const newFiles = files
      .filter((f) => !f.existingId)
      .map(asInput);
    let res;
    try {
      if (editing && itemId) {
        // Gallery images to (re)send: new uploads, plus the previous mainImage
        // if it was demoted to the gallery (resent under its new role).
        const galleryNew = images
          .filter((f) => f !== main && !f.existingId)
          .map(asInput);
        const demotedMain = images.find((f) => f.existingRole === "main" && f !== main);
        if (demotedMain?.content) galleryNew.push(asInput(demotedMain));
        // An existing gallery image promoted to mainImage: resent as mainImage,
        // its gallery copy is deleted.
        const promotedGalleryId =
          main?.existingRole === "gallery" ? (main.existingId ?? null) : null;
        const mainUnchanged = main?.existingRole === "main";
        const data = {
          ...(!actingAsCompanyId && sharingEdits !== null
            ? { sharedWith: recipientRelations(selectedGroups) } : {}),
          ...(!actingAsCompanyId && publiclyReadable !== (editItem?.publiclyReadable === true)
            ? { publiclyReadable } : {}),
          name: name.trim(),
          shortDescription: shortDescription.trim() || null,
          description: description || null,
          ...(main && !mainUnchanged && main.content ? { mainImage: asInput(main) } : {}),
          images: galleryNew,
          files: newFiles,
          imagesToDelete: [
            ...removedImageIds,
            ...(promotedGalleryId ? [promotedGalleryId] : []),
          ],
          filesToDelete: removedFileIds,
        };
        res = await updateItem({
          // THE ACTING CONTEXT RIDES ALONG SINCE CONCEPT v23 (0002 §6, Kevin 2026-09-11): changing a
          // company's post or project takes a current admin membership AND acting as that company,
          // and genesis refuses the write when the declaration is absent or names another company.
          //
          // Preserve the actual mode even for personal content on an already-open edit page.
          // Genesis must see the mismatch to return GENESIS001260.
          //
          // THE SAME VALUE THE CREATE BRANCH SIGNS WITH, deliberately. `company` is creation-only, so
          // an item's voice never changes — what v23 adds is that the EDIT has to be made in that
          // voice too, and reading one variable for both keeps the two from ever disagreeing about
          // which company this page is acting as.
          variables: {
            id: itemId,
            input: {
              data,
              dryRun: false,
              ...actingAsDeclaration(actingAsCompanyId),
            },
          },
          refetchQueries: ["SharedItemDetail"],
          update(cache: ApolloCache) {
            // Force any /share or /save list query to re-resolve from the
            // network on next observation — UpdateSharedItem's response only
            // selects { id, name }, so list cards would otherwise show stale
            // shortDescription / mainImage / engagement counts.
            cache.evict({ fieldName: "sharedItems" });
            cache.gc();
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      } else {
        // POSTING AS THE COMPANY. The context switch is the condition, not the decoration
        // (the company account model): whichever company the switcher is in
        // is the one the item belongs to, and in personal context the same person writes as
        // themselves. genesis re-checks the membership row and refuses a caller who is not an admin
        // of THAT company, so this field is a statement of intent, never the authorisation.
        //
        // SET ONLY HERE. `company` is creation-only in the schema — a personal post cannot become a
        // company post later and a company post cannot be taken private, because the two live in
        // different places and a switch would move content between them mid-life. The edit branch
        // above deliberately does not send it.
        const data = {
          itemType,
          ...(!actingAsCompanyId
            ? { sharedWith: recipientRelations(selectedGroups), publiclyReadable } : {}),
          name: name.trim(),
          shortDescription: shortDescription.trim() || null,
          description: description || null,
          ...(actingAsCompanyId ? { company: { id: actingAsCompanyId } } : {}),
          ...(main ? { mainImage: asInput(main) } : {}),
          images: images.filter((f) => f !== main).map(asInput),
          files: newFiles,
        };
        res = await createItem({
          // AND THE ENVELOPE SAYS IT TOO, since Kevin extended v23 to creating on 2026-09-12 („Auch
          // das ANLEGEN, und damit ist v23 ohne Ausnahme"). Spelt from the SAME value as
          // `data.company` two lines above, so the two cannot contradict each other — a create that
          // declares a context must produce content belonging to that company, and genesis refuses a
          // declaration over a private post with GENESIS001260. Absent in personal context, which is
          // the create that stays allowed and keeps every pre-v23 caller working.
          variables: {
            input: {
              data, dryRun: false,
              ...(!actingAsCompanyId && onlyOwner ? { privateToOwner: true } : {}),
              ...(actingAsCompanyId ? { actingAsCompanyId } : {}),
            },
          },
          update(cache: ApolloCache) {
            // CreateSharedItem returns only { id, name } — Apollo can't insert
            // the new entity into any cached SharedItems list. Evict the list
            // field so /share refetches it the next time it mounts.
            cache.evict({ fieldName: "sharedItems" });
            cache.gc();
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      }
    } catch (e) {
      // THREE FAILURES HERE HAVE AN ACTIONABLE REPAIR, and each points somewhere else: a frozen
      // company is repaired by reactivating it, a company post saved from personal context by
      // switching INTO the company, and one's own post saved from a company context by LEAVING it
      // (concept v23, both directions). Everything else keeps the generic sentence.
      throw new LocalizedError(
        companyFrozenMessage(intl, e) ??
          notActingAsCompanyMessage(intl, e) ??
          // AND ITS MIRROR, which points the other way: leave the context rather than enter one. A
          // page opened on one's own post and left standing while a second pane entered a company
          // reaches exactly this, and answering it with the "act as this company" sentence would send
          // the reader further from the save they are attempting.
          actingAsCompanyOnOwnContentMessage(intl, e) ??
          userErrorMessage(intl, e),
        { cause: e },
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = editing
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (res?.data as any)?.updateSharedItem
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (res?.data as any)?.createSharedItem;
    if (payload?.issues?.length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      throw new LocalizedError(payload.issues.map((i: any) => i.message).join(" · "));
    }
    return payload?.result?.id ?? itemId ?? null;
  }

  const pending = usePendingEdits();
  async function publish() {
    if (!name.trim() || loading) return;
    setError(null);
    try {
      // Counted as a running save, so the leave dialog waits for it rather than offering a Save.
      const id = await (pending ? pending.trackSave(persist()) : persist());
      // Clean in the registry at once, not only when the remounted form's effect registers: the
      // blocker asks the registry the moment the navigation below fires (spark#1213). That also lets
      // a held navigation through. If the reader pressed ← while the write ran, that held navigation
      // is theirs, and navigating here as well would replace it — the router keeps only one.
      settleScope();
      onReset();
      if (pending?.isLeaveHeld()) return;
      navigate(id ? `/share/discover/${id}` : "/extended-search?type=SHARE");
    } catch (e) {
      const localized = localizedMessageOf(e);
      if (!localized) logApplicationFailure("share.publish");
      setError(localized ?? userErrorMessage(intl, e));
    }
  }

  // Eine Definition, zwei Orte: in der Formularbox (Bearbeiten) und unter der
  // Vorschau. Die Aktionen liegen damit dort, wo auch das Anlegen eines Projekts
  // sie hat - innerhalb der Box, nicht darunter im Nichts.
  const actionRow = (
    <>
      <Link
        to={editing && itemId ? `/share/discover/${itemId}` : "/share"}
        className={buttonClasses({ variant: "secondary" })}
      >
        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
      </Link>
      <Button id="share-create-preview" variant="secondary" onClick={togglePreview}>
        {preview ? (
          <FormattedMessage id="common.edit" defaultMessage="Edit" />
        ) : (
          <FormattedMessage id="share.create.previewButton" defaultMessage="Preview" />
        )}
      </Button>
      <Button
        id="share-create-publish"
        variant="primary"
        onClick={() => void publish()}
        disabled={!name.trim() || loading || (!actingAsCompanyId && sharingUnavailable)}
        className="disabled:opacity-40"
      >
        {loading ? "…" : editing || (!actingAsCompanyId && onlyOwner) ? (
          <FormattedMessage id="common.save" defaultMessage="Save" />
        ) : (
          <FormattedMessage id="share.create.publish" defaultMessage="Publish" />
        )}
      </Button>
    </>
  );

  return (
    <div
      className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="Myself, CreateSharedItem"
    >
      <div className="mb-5">
        <BackButton />
      </div>

      <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
        SHARE ·{" "}{/* i18n-allow: SHARE is a feature/brand name; · is a glyph */}
        {preview ? (
          <FormattedMessage id="share.create.eyebrowPreview" defaultMessage="Preview" />
        ) : editing ? (
          <FormattedMessage id="share.create.eyebrowEdit" defaultMessage="Edit" />
        ) : (
          <FormattedMessage id="share.create.eyebrowCreate" defaultMessage="Create" />
        )}
      </div>
      <h1 className="smycles-page-title mb-6">
        {preview ? (
          <FormattedMessage id="share.create.previewHeading" defaultMessage="Here's how it looks" />
        ) : editing ? (
          <FormattedMessage
            id="share.create.editHeading"
            defaultMessage="{type, select, POST {Edit post} other {Edit project}}"
            values={{ type: itemType }}
          />
        ) : (
          <FormattedMessage
            id="share.create.createHeading"
            defaultMessage="{type, select, POST {New post} other {New project}}"
            values={{ type: itemType }}
          />
        )}
      </h1>

      {/*
        WHOSE VOICE THIS IS. The concept requires the composer to say so rather than decide silently:
        "the composer's author control is admin-only and context-bound — it appears in company
        context, for an admin, and nowhere else. A plain member has no path to it, so the control is
        absent rather than disabled." Sending the company without showing it was the worse half of
        both worlds: an admin who had entered company context to edit the profile could compose a
        personal note and find it published in the company's name with no warning.

        NOT ON THE EDIT PATH. `company` is creation-only, so an existing item's voice cannot change
        and offering the reader a control over it would be a lie.

        A neutral note, not a warning: this is a state, not a problem.
      */}
      {!editing && actingAsCompanyId && activeCompany ? (
        <div
          id="share-create-actingas"
          className="mb-6 rounded border border-ink/10 bg-ink/[0.02] px-3 py-2 text-smycles-label text-ink"
        >
          <FormattedMessage
            id="share.create.actingAsCompany"
            defaultMessage="Posting as {company}. Leave the company context to post as yourself."
            values={{ company: <span className="font-medium">{activeCompany.name}</span> }}
          />
        </div>
      ) : null}

      {preview ? (
        <SharePreview
          itemType={itemType}
          name={name}
          shortDescription={shortDescription}
          description={description}
          images={[...images].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0))}
          authorName={myFullName}
          company={actingAsCompanyId && activeCompany ? { id: activeCompany.id, name: activeCompany.name } : null}
        />
      ) : (
        <div className="box max-w-3xl space-y-5 bg-surface p-5 @sm:p-6">
          <div>
            <div className="mb-1.5 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="share.create.typeLabel" defaultMessage="Type" />
            </div>
            {editing || fixedItemType ? (
              <span className="inline-block rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
                {itemTypeLabel(itemType)}
              </span>
            ) : (
            <SegmentedControl
              id="share-create-type"
              value={itemType}
              onChange={setItemType}
              options={[
                {
                  value: "PROJECT",
                  label: intl.formatMessage({
                    id: "share.create.typeProject",
                    defaultMessage: "Project",
                  }),
                },
                {
                  value: "POST",
                  label: intl.formatMessage({
                    id: "share.create.typePost",
                    defaultMessage: "Post",
                  }),
                },
              ]}
            />
            )}
          </div>
          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="share.create.titleLabel" defaultMessage="Title" />
            </div>
            <input
              id="share-create-title"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={intl.formatMessage(
                {
                  id: "share.create.titlePlaceholder",
                  defaultMessage: "{type, select, POST {Title of the post} other {Title of the project}}",
                },
                { type: itemType },
              )}
              className={inputCls}
            />
          </div>

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="share.create.shortDescriptionLabel" defaultMessage="Short description" />
            </div>
            <input
              id="share-create-short"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder={intl.formatMessage({
                id: "share.create.shortDescriptionPlaceholder",
                defaultMessage: "In one sentence: what is it about?",
              })}
              className={inputCls}
            />
          </div>

          <FileDrop
            idPrefix="share-images"
            label={intl.formatMessage({ id: "share.create.imagesLabel", defaultMessage: "Images" })}
            accept="image/*"
            items={images}
            onChange={changeImages}
            onAppend={appendImages}
            onSelectMain={selectMainImage}
            maxBytes={MAX_SHARE_IMAGE_BYTES}
            headerAction={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDocPicker("image")}
              >
                <FormattedMessage
                  id="share.create.fromDocuments"
                  defaultMessage="From my documents"
                />
              </Button>
            }
          />

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="share.create.descriptionLabel" defaultMessage="Description" />
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={10}
              placeholder={intl.formatMessage(
                {
                  id: "share.create.descriptionPlaceholder",
                  defaultMessage: "{type, select, POST {Describe your post …} other {Describe your project …}}",
                },
                { type: itemType },
              )}
              aria-describedby="share-description-markdown-hint"
              className={`${inputCls} resize-y leading-relaxed`}
            />
            <div id="share-description-markdown-hint" className="smycles-hint mt-2">
              <div className="text-smycles-micro tracking-[0.2em] text-ink-accent">
                <FormattedMessage id="share.create.markdownHintTitle" defaultMessage="Formatting" />
              </div>
              <div className="mt-1 smycles-field-value text-ink">
                <FormattedMessage
                  id="share.create.markdownHint"
                  defaultMessage="Markdown works: **bold**, _italic_, lists and links. A pasted URL becomes clickable."
                />
              </div>
            </div>
          </div>

          <FileDrop
            idPrefix="share-attachments"
            label={intl.formatMessage({ id: "share.create.attachmentsLabel", defaultMessage: "Attachments" })}
            // Not "*/*" — see PROJECT_DOCUMENT_ACCEPT. The attachment is later opened as a blob: URL,
            // which inherits spark's origin, so an unconstrained type is the worst of the upload gaps.
            accept={PROJECT_DOCUMENT_ACCEPT}
            items={files}
            onChange={changeFiles}
            onAppend={appendFiles}
            maxBytes={MAX_SHARE_ATTACHMENT_BYTES}
            headerAction={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDocPicker("file")}
              >
                <FormattedMessage
                  id="share.create.fromDocuments"
                  defaultMessage="From my documents"
                />
              </Button>
            }
          />

          {error ? <div className="text-smycles-meta text-ink-accent">{error}</div> : null}

      {!actingAsCompanyId && <div className="mt-6 space-y-2">
        <h2 className="text-smycles-title font-medium"><FormattedMessage id="share.create.readershipTitle" /></h2>
        <SharedItemSharingFields id="share-create-sharing" selection={{ ids: selectedGroups, requiredIds: requiredDefaults ? recipientIds(defaultGroups) : [], recipients: [...recipients.values()],
          loading: sharingUnavailable, error: sharingError ? userErrorMessage(intl, sharingError) : undefined,
          onChange: (ids) => setSharingEdits([...new Set([...ids,
            ...(requiredDefaults ? recipientIds(defaultGroups) : [])])]),
        }} publication={{ value: publiclyReadable, onChange: setPubliclyReadable }} />
        {requiredDefaults && !publiclyReadable && <p className="text-smycles-meta text-ink-muted">
          <FormattedMessage id="share.create.requiredGroups" />
        </p>}
        {onlyOwner && <p className="text-smycles-meta text-ink-muted">
          <FormattedMessage id="share.create.privateHint" defaultMessage="Only you can read this item." />
        </p>}
        {sharingError && <div role="alert">
          <p className="text-smycles-meta">{userErrorMessage(intl, sharingError)}</p>
          <Button variant="ghost" onClick={() => { void sharing.refetch(); void policy.refetch(); }}>
            <FormattedMessage id="common.retry" />
          </Button>
        </div>}
      </div>}

          <div className="flex flex-wrap items-center justify-end gap-2">{actionRow}</div>
        </div>
      )}

      <DocumentPickerDialog
        open={docPicker !== null}
        kind={docPicker ?? "file"}
        onClose={() => setDocPicker(null)}
        onPick={addPickedDocuments}
      />

      {/* Im Vorschau-Modus gibt es keine Formularbox, in die Fehler und Aktionen
          gehören könnten: die Vorschau ist ein dreispaltiges Raster aus eigenen
          Boxen. Dort bleiben beide deshalb unter dem Inhalt stehen. Im
          Formular-Modus stehen sie IN der Box, sonst stünde die Meldung zweimal
          auf der Seite. */}
      {preview ? (
        <>
          {error ? <div className="mt-4 text-smycles-meta text-ink-accent">{error}</div> : null}
          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">{actionRow}</div>
        </>
      ) : null}
    </div>
  );
}

function SharePreview({
  itemType,
  name,
  shortDescription,
  description,
  images,
  authorName,
  company,
}: {
  itemType: ItemType;
  name: string;
  shortDescription: string;
  description: string;
  images: UploadedFile[];
  authorName: string;
  /** The company this will be published as, or null when posting as oneself. */
  company: { id: string; name: string } | null;
}) {
  const intl = useIntl();
  const gallery = images.filter((f) => Boolean(f.preview));
  const [activeIdx, setActiveIdx] = useState(0);
  const hero = gallery[activeIdx] ?? null;
  const now = formatDateTime(new Date(), intl.locale);

  return (
    <div className="grid grid-cols-1 gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)_minmax(260px,320px)] @lg:items-start">
      <aside>
        <div className="box bg-surface p-5">
          <div className="space-y-4">
            {/* THE PREVIEW HAS TO SHOW WHOSE POST IT WILL BE. This aside reproduces the detail
                page's meta block, and that page puts "Published by <company>" ABOVE the author for a
                company post (concept v10, 0002 §6) — so a preview that omits it shows a company post
                as a personal one, which is exactly the thing the composer's own note above says is
                about to happen. */}
            {company ? (
              <div>
                <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                  <FormattedMessage
                    id="share.itemDetail.publishedBy"
                    defaultMessage="Published by"
                  />
                </div>
                <div className="text-smycles-body font-medium">{company.name}</div>
              </div>
            ) : null}
            <div>
              <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                <FormattedMessage id="share.create.authorLabel" defaultMessage="Author" />
              </div>
              <div className="text-smycles-body font-medium">{authorName || "–"}</div>
            </div>
            <div>
              <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                <FormattedMessage id="share.create.publishedAtLabel" defaultMessage="Published on" />
              </div>
              <div className="text-smycles-body">{now}</div>
            </div>
            <div>
              <div className="mb-1.5 text-smycles-micro tracking-[0.25em] text-ink-muted">
                <FormattedMessage id="share.create.ratingLabel" defaultMessage="Rating" />
              </div>
              <div className="inline-flex gap-0.5 text-smycles-title leading-none">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className="text-ink-muted">★</span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-5 space-y-2.5 border-t border-ink/5 pt-4">
            <PreviewStat
              icon="eye"
              label={intl.formatMessage({ id: "share.create.statViews", defaultMessage: "Views" })}
            />
            <PreviewStat
              icon="pin"
              label={intl.formatMessage({ id: "share.create.statPins", defaultMessage: "Pins" })}
            />
            <PreviewStat
              icon="star"
              label={intl.formatMessage({ id: "share.create.statRatings", defaultMessage: "Ratings" })}
            />
            <PreviewStat
              icon="comment"
              label={intl.formatMessage({ id: "share.create.statComments", defaultMessage: "Comments" })}
            />
            <PreviewStat
              icon="hand"
              label={intl.formatMessage({ id: "share.create.statInterested", defaultMessage: "Interested" })}
            />
          </div>
        </div>
      </aside>

      <section>
        <div className="box bg-surface p-5 @sm:p-6">
          <span className="inline-block rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
            {itemTypeLabel(itemType)}
          </span>
          <h1 className="smycles-page-title mt-3">
            {name || intl.formatMessage({ id: "share.create.untitled", defaultMessage: "Untitled" })}
          </h1>
          {shortDescription ? (
            <p className="mt-3 max-w-2xl text-smycles-body leading-relaxed text-ink">{shortDescription}</p>
          ) : null}

          {hero ? (
            <div className="mt-5">
              <div className="overflow-hidden rounded-md bg-ink/[0.06] ring-1 ring-ink/5">
                <DocumentView variant="image" file={hero.document ?? hero} previewUrl={hero.preview} className="aspect-[16/9] w-full" />
              </div>
              {gallery.length > 1 ? (
                <div id="share-preview-gallery" className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {gallery.map((f, i) => (
                    <div key={i} className={`shrink-0 overflow-hidden rounded-md ring-1 transition ${
                      i === activeIdx ? "smycles-selected ring-2 ring-accent-decoration" : "ring-ink/10 hover:ring-accent-decoration/40"
                    }`}>
                      <DocumentView variant="image" file={f.document ?? f} previewUrl={f.preview}
                        onSelect={() => setActiveIdx(i)} className="h-16 w-24" />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-6 border-t border-ink/5 pt-5">
            {description ? (
              <MarkdownText content={description} className="text-smycles-body leading-relaxed text-ink" />
            ) : (
              <div className="text-smycles-meta text-ink-muted">
                <FormattedMessage id="share.create.noDescription" defaultMessage="No description." />
              </div>
            )}
          </div>
        </div>
      </section>

      <aside>
        <div className="box bg-surface p-5">
          <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="share.create.commentsLabel" defaultMessage="Comments" />
          </div>
          <div className="text-smycles-meta text-ink-muted">
            <FormattedMessage
              id="share.create.commentsAfterPublish"
              defaultMessage="Available after publishing."
            />
          </div>
        </div>
      </aside>
    </div>
  );
}

function PreviewStat({ icon, label }: { icon: Parameters<typeof ShareIcon>[0]["name"]; label: string }) {
  return (
    <div className="flex items-center gap-2.5 text-smycles-meta">
      <span className="w-6 tabular-nums text-ink">0</span>
      <ShareIcon name={icon} className="h-3.5 w-3.5 text-ink-muted" />
      <span className="text-ink-muted">{label}</span>
    </div>
  );
}

export function FileDrop({
  idPrefix,
  label,
  accept,
  items,
  onChange,
  onAppend,
  headerAction,
  maxBytes,
  onSelectMain,
}: {
  /** Testanker; unbesetzt, wo kein Test hingreift. */
  idPrefix?: string;
  label: string;
  accept: string;
  items: UploadedFile[];
  onChange: (next: UploadedFile[]) => void;
  onAppend: (next: UploadedFile[]) => void;
  headerAction?: ReactNode;
  maxBytes: number;
  /** Image grids only: click marks a tile as the item's mainImage. */
  onSelectMain?: (index: number) => void;
}) {
  const intl = useIntl();
  const zoneRef = useRef<FileDropZoneHandle>(null);
  const [busy, setBusy] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);

  async function ingest(fileList: FileList | null) {
    setBusy(true);
    try {
      const result = await ingestFiles(fileList, { maxBytes, accept });
      const unsupported = result.rejected.filter((r) => r.reason === "unsupportedType");
      const tooLarge = result.rejected.filter((r) => r.reason === "tooLarge");
      if (unsupported.length > 0) {
        setLimitError(
          intl.formatMessage(
            {
              id: "share.create.unsupportedType",
              defaultMessage: "Unsupported file type: {names}",
            },
            { names: unsupported.map((r) => r.file.name).join(", ") },
          ),
        );
      } else if (tooLarge.length > 0) {
        setLimitError(
          intl.formatMessage(
            {
              id: "share.create.tooLarge",
              defaultMessage: "Too large (max. {max} MB per file): {names}",
            },
            {
              max: Math.floor(maxBytes / (1024 * 1024)),
              names: tooLarge.map((r) => r.file.name).join(", "),
            },
          ),
        );
      } else {
        setLimitError(null);
      }
      if (result.accepted.length === 0) return;
      const accepted = result.accepted.map(toUploadedFile);
      onAppend(accepted);
    } catch {
      logApplicationFailure("share.file-drop-read");
    } finally {
      setBusy(false);
    }
  }

  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <div id={idPrefix && `${idPrefix}-count`} className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          {label} · {items.length}
        </div>
        <div className="flex items-center gap-2">
          {headerAction}
          <Button
            variant="primary"
            size="sm"
            onClick={() => zoneRef.current?.openPicker()}
          >
            + <FormattedMessage id="common.add" defaultMessage="Add" />
          </Button>
        </div>
      </div>

      <FileDropZone
        ref={zoneRef}
        id={idPrefix}
        inputId={idPrefix && `${idPrefix}-file`}
        variant="zone"
        accept={accept}
        clickToOpen={items.length === 0}
        onFiles={(fl) => void ingest(fl)}
        label={
          busy ? (
            <FormattedMessage id="share.create.loadingFiles" defaultMessage="loading…" />
          ) : undefined
        }
      >
        {items.length > 0 ? (
          <div className="p-3">
            <div className="grid grid-cols-3 gap-2 @sm:grid-cols-4 @lg:grid-cols-5">
              {items.map((f, i) => (
                <DocumentView variant="tile"
                  file={f.document ?? (f.content ? f : createUnavailableDocumentResource({ name: f.name, mimeType: f.mimeType, reason: "original-missing" }))}
                  key={i}
                  idPrefix={idPrefix && `${idPrefix}-${f.name}`}
                  name={f.name}
                  previewUrl={f.preview || null}
                  selected={f.isMain}
                  badge={
                    f.isMain ? (
                      <span className="rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
                        <FormattedMessage
                          id="share.create.mainImageBadge"
                          defaultMessage="Preview image"
                        />
                      </span>
                    ) : undefined
                  }
                  onClick={onSelectMain ? () => onSelectMain(i) : undefined}
                  title={
                    onSelectMain
                      ? intl.formatMessage({
                          id: "share.create.mainImageHint",
                          defaultMessage: "Set as preview image",
                        })
                      : undefined
                  }
                  onRemove={() => remove(i)}
                />
              ))}
            </div>
          </div>
        ) : null}
      </FileDropZone>
      {limitError ? <div id={idPrefix && `${idPrefix}-error`} className="mt-1 text-smycles-meta text-ink-accent">{limitError}</div> : null}
    </div>
  );
}

/** Everything the reader can change on the form, as one comparable string. */
function formSnapshot(form: {
  name: string;
  shortDescription: string;
  description: string;
  images: UploadedFile[];
  files: UploadedFile[];
  publiclyReadable: boolean;
}): string {
  // A stored file is its id; a new one has no id yet, so its name and size stand in.
  const fileKey = (f: UploadedFile) => [f.existingId ?? `${f.name}:${f.content.length}`, f.isMain === true];
  return JSON.stringify({
    ...form,
    images: form.images.map(fileKey),
    files: form.files.map(fileKey),
  });
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join("\n") === [...b].sort().join("\n");
}
